import { Box, Button, CircularProgress, Divider, Grid, Hidden, IconButton, Skeleton, Theme, Tooltip, Typography, useTheme } from "@mui/material"
import AlertModel from "../../common/AlertModel"
import { checkImageExists, formatCurrency } from "../../../../utils/utilityFunctions"
import { BlueEditIcon, DeleteIcons, DragAndDropIcon, InfoIconOutlined } from "../../../../assets/SvgIcons"
import { currencyCodeMap, ENABLE_MF_TRANSACTIONS_POA_DOCUMENT_UPLOAD, MPOWER_ACCOUNT_TYPE_MASTER, USER_ROLES } from "../../../../utils/constant"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RootStateType } from "../../../../redux-store/reducers"
import { MandateCreation } from "../../../../redux-store/types/investorProfile"
import { useHistory } from "react-router"
import { MpowerValidationType, SwitchNavigationProps } from "../../types"
import { FormikProps, FormikValues, useFormikContext } from '../../../../lib/formik/Formik'
import { GETINVESTMENTDETAILTYPE, InvestmentFundType } from "../../../../redux-store/types/api-types"
import { CustomeBlueDatePicker, CustomeBlueDropDown, CustomeNumberInput, FormCheckBox } from "../../transcation/FromInput"
import FromSchemeDialog from "./FromSchemeDialog"
import { HoldingsTableData } from "../holdings/types"
import { getInvestmentDetails, getStpWeekDays } from "../../../../redux-store/actions/funds"
import { setSelectedAllHoldingsInfo, setSelectedHoldingsInfo } from "../../../../redux-store/actions/investorProfile"
import IncomingSchemeCard from "./IncomingSchemeCard"
import { StpWeekDaysObj } from "../../../../redux-store/types/funds"

interface CardProps {
    id: number;
    bankDetails: MandateCreation[]
    isLoading: boolean;
    isSubmitting: boolean;
    handleDelete: (checkId: string, selectedUniqueId: string) => void,
}

const Card = (props: CardProps): JSX.Element => {
    const dispatch = useDispatch()
    const { palette, typography } = useTheme()
    const history = useHistory()
    const { pathname, state } = history.location
    const navState = state as SwitchNavigationProps

    const isSwitchScreen = pathname.split('/')[pathname.split('/').length - 1] === 'switch'

    const [open, setOpen] = useState(false)
    const [isFromSchemeDialogOpen, setFromSchemeDialogOpen] = useState<boolean>(false)
    const [schemeLoading, setSchemeLoading] = useState(false)

    const { id, bankDetails, isSubmitting, handleDelete, isLoading } = props;

    const formik = useFormikContext() as FormikProps<any>
    const { errors, values, setFieldValue, setFieldError, validateField, initialValues, resetForm } = formik
    const { fund } = values
    const {
        outgoingSchInvDetails,
        outgoingSchDetails,
        incomingSchInvDetails,
        switchTo,
        isSwitchTransactionNotAllowed
    }: {
        outgoingSchInvDetails: InvestmentFundType;
        outgoingSchDetails: HoldingsTableData;
        incomingSchInvDetails: InvestmentFundType;
        switchTo: boolean;
        isSwitchTransactionNotAllowed?: boolean
    } = fund[id]

    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);
    const {
        selectedPortfolio,
        selectedPortfolioObj
    } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);
    const {
        role = ''
    } = useSelector((store: RootStateType) => store.auth);
    const {
        selectAllHoldingsInfo,
        selectedHoldingsInfo
    } = useSelector((store: RootStateType) => store.investorProfile);

    const Daily = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'DAILY' && item.transactionType === 'STP')
    const Weekly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'WEEKLY' && item.transactionType === 'STP')
    const WeeklyDays = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'WEEKLYDAYS' && item.transactionType === 'STP')
    const Monthly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'MONTHLY' && item.transactionType === 'STP')
    const Quarterly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'QUARTERLY' && item.transactionType === 'STP')
    const Yearly = incomingSchInvDetails?.validations?.find((item) => item?.frequency === 'YEARLY' && item.transactionType === 'STP')

    const SwitchValidate = incomingSchInvDetails?.validations?.find((item) => item.transactionType === 'RED')


    const stpMinAmount = (key = 'minimumInstallmentAmount' as keyof MpowerValidationType) => {

        if (fund?.[id]?.frequency === 'WEEKLY') {
            return Weekly?.[key]
        } else if (fund?.[id]?.frequency === 'MONTHLY') {
            return Monthly?.[key]
        } else if (fund?.[id]?.frequency === 'QUARTERLY') {
            return Quarterly?.[key]
        } else if (fund?.[id]?.frequency === 'YEARLY') {
            return Yearly?.[key]
        } else if (fund?.[id]?.frequency === 'DAILY') {
            return Daily?.[key]
        } else if (fund?.[id]?.frequency === 'WEEKLYDAYS') {
            return WeeklyDays?.[key]
        } else {
            return 0
        }
    }

    const handleChangeScheme = () => {
        setFromSchemeDialogOpen(true)
    }

    const getValidation = async (id: number, shemeId: string, divReinvestAllowed = "") => {
        try {
            setSchemeLoading(true);

            setFieldValue(`fund[${id}]switchTo`, true)

            setFromSchemeDialogOpen(false)

            const payload = {
                schemeIds: [shemeId],
                portfolioId: selectedPortfolio?.value,
                clientCode: navState.clientCode,
                rmId: `${investorRmId}`,
                schemeDetails: [{ scheme_id: shemeId, isReinvestment: divReinvestAllowed ?? "-" }]
            }

            const [
                { value: investmentDetailsRes },
                { value: stpWeekDaysRes = [] }
            ] = (await Promise.allSettled([
                dispatch(getInvestmentDetails(payload)),
                dispatch(getStpWeekDays())
            ])) as [
                    { value: unknown | GETINVESTMENTDETAILTYPE },
                    { value: unknown | Array<StpWeekDaysObj> }
                ];

            resetForm({
                values: {
                    ...(isSwitchScreen ? {
                        fund: (values?.fund ?? [])?.map((item: FormikValues, index: number) => {
                            if (id === index) {
                                return {
                                    ...item,
                                    switchValue: 'amount',
                                    installment: '',
                                    // bankType: '',
                                    entireBalance: false,
                                    switchTo: true,
                                    incomingSchInvDetails: (
                                        (investmentDetailsRes as GETINVESTMENTDETAILTYPE)?.investmentDetails?.[0] ?? {}
                                    )
                                }
                            } else return item
                        })
                    } : {
                        fund: (values?.fund ?? [])?.map((item: FormikValues, index: number) => {
                            if (id === index) {
                                const incomingSchWeekDays = (
                                    (stpWeekDaysRes as Array<StpWeekDaysObj>) ?? []
                                )?.find(obj =>
                                    `${obj?.companyId ?? ""}` ===
                                    `${(investmentDetailsRes as GETINVESTMENTDETAILTYPE)?.investmentDetails?.[0]?.fundAmcId ?? ""}`
                                ) ?? {}

                                return {
                                    ...item,
                                    amount: '',
                                    frequency: (investmentDetailsRes as GETINVESTMENTDETAILTYPE)
                                        ?.investmentDetails
                                        ?.[0]
                                        ?.validations
                                        ?.find((item) => item?.transactionType === 'STP' && item.frequency)
                                        ?.frequency
                                        ?? '',
                                    swpDate: '',
                                    installment: '',
                                    stpEndDate: '',
                                    firstOrder: false,
                                    switchTo: true,
                                    incomingSchInvDetails: (
                                        (investmentDetailsRes as GETINVESTMENTDETAILTYPE)?.investmentDetails?.[0] ?? {}
                                    ),
                                    incomingSchWeekDays
                                }
                            } else return item
                        })
                    }),
                    ...((
                        ENABLE_MF_TRANSACTIONS_POA_DOCUMENT_UPLOAD &&
                        ![USER_ROLES.INVESTOR].includes(role) &&
                        MPOWER_ACCOUNT_TYPE_MASTER[selectedPortfolioObj?.portfolioAccountType?.toUpperCase()] ===
                        MPOWER_ACCOUNT_TYPE_MASTER["POA"]
                    ) ? {
                        documents: values?.documents ?? []
                    } : {})

                }
            })
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setSchemeLoading(false)
        }
    }

    const handleDeleteCardValue = (scheme: string, selectedUniqueId: string) => {
        const modifiedSelectedHoldingsInfo = selectedHoldingsInfo.filter(({ uniqueId }) => (uniqueId !== selectedUniqueId))
        const modifiedSelectedAllHoldingsInfo = selectAllHoldingsInfo.filter(({ uniqueId }) => (uniqueId !== selectedUniqueId))

        dispatch(setSelectedHoldingsInfo([...modifiedSelectedHoldingsInfo]))
        dispatch(setSelectedAllHoldingsInfo([...modifiedSelectedAllHoldingsInfo]))

        history.replace({
            state: ({
                selectedSchemes: [...modifiedSelectedAllHoldingsInfo],
                clientCode: navState?.clientCode ?? "",
                schemeDetails: modifiedSelectedAllHoldingsInfo?.map((obj) => ({
                    scheme_id: obj?.schemeId ?? "-",
                    isReinvestment: obj?.isReinvest ?? "-"
                })),
                folio: [...modifiedSelectedHoldingsInfo],
            })
        })

        const newValues = values.fund.filter((item: any) => {
            return (item?.outgoingSchDetails?.uniqueId !== selectedUniqueId)
        })

        resetForm({
            values: {
                fund: newValues,
                ...((
                    ENABLE_MF_TRANSACTIONS_POA_DOCUMENT_UPLOAD &&
                    ![USER_ROLES.INVESTOR].includes(role) &&
                    MPOWER_ACCOUNT_TYPE_MASTER[selectedPortfolioObj?.portfolioAccountType?.toUpperCase()] ===
                    MPOWER_ACCOUNT_TYPE_MASTER["POA"]
                ) ? {
                    documents: values?.documents ?? []
                } : {})
            }
        })

        handleDelete(scheme, selectedUniqueId)
    }

    return (
        <>
            <Box
                sx={{
                    background: palette.background.paper,
                    boxShadow: 1,
                    borderRadius: '20px',
                    padding: '35px 30px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '35px'
                }}
            >
                <Typography
                    variant='h2'
                    color={palette.text.primary}
                    fontSize={{
                        sm: '18px',
                        xs: '18px',
                        md: '20px',
                        lg: '20px'
                    }}
                >
                    {isSwitchScreen ? 'Switch' : 'Transfer'} From
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'row',
                            columnGap: '20px',
                            overflow: 'hidden'
                        }}
                    >
                        {(isLoading) ? (
                            <CircularProgress />
                        ) : (
                            <img
                                src={checkImageExists(`${outgoingSchInvDetails?.fundAmcId ?? ""}`) ?
                                    `/images/amcIcons/${outgoingSchInvDetails?.fundAmcId ?? ""}.svg` :
                                    `/images/amcIcons/default.svg`}
                                width={'50px'}
                                height={'50px'}
                                alt="Bank name"
                                style={{ borderRadius: '8px 8px 8px 8px' }}
                            />
                        )}
                        <Box>
                            {isLoading ? (
                                <Skeleton variant='text' animation="wave" width={200} />
                            ) : (
                                <Typography
                                    variant='body_600'
                                    color="text.primary"
                                    sx={{ maxWidth: '100%' }}
                                >
                                    {outgoingSchInvDetails?.fundName ?? ""}
                                </Typography>
                            )}
                            {isLoading ? (
                                <Skeleton variant='text' animation="wave" width={200} />
                            ) : (
                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    gap={'5px'}
                                >
                                    <Typography
                                        variant='body_500'
                                        color={palette.text.secondary}
                                        sx={{
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {outgoingSchInvDetails?.fundType ?? ""}
                                    </Typography>
                                    <Typography
                                        variant='body_500'
                                        color={palette.text.secondary}
                                        sx={{
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            maxWidth: { lg: '100%', md: '80%' },
                                        }}
                                    >
                                        • {outgoingSchInvDetails?.fundCap ?? ""}
                                    </Typography>
                                    {(
                                        `${outgoingSchInvDetails?.riskType ?? ""}`.toLowerCase() !== "null" || outgoingSchInvDetails?.riskType
                                    ) && (
                                            <Typography
                                                variant='body_500'
                                                color={palette.success[800]}
                                                sx={{
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                • {outgoingSchInvDetails?.riskType ?? ""}
                                            </Typography>
                                        )}
                                </Box>
                            )}
                            {isLoading ? (
                                <Skeleton variant='text' animation="wave" width={200} />
                            ) : (
                                <Box sx={{ marginTop: "3px" }}>
                                    <Typography
                                        variant="body_500"
                                        color="text.primary"
                                    >
                                        ISIN:{' '}
                                        <Typography
                                            component="span"
                                            variant="body_500"
                                            color="text.secondary"
                                        >
                                            {outgoingSchInvDetails?.isinCode ?? ""}
                                        </Typography>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    {(isLoading) ? (
                        <Skeleton variant="text" animation="wave" width={100} />
                    ) :
                        <IconButton
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}
                            onClick={() => handleDeleteCardValue(outgoingSchDetails?.id ?? "", outgoingSchDetails?.uniqueId ?? "")}
                            disabled={isSubmitting}
                            aria-label='Delete'
                        >
                            <DeleteIcons />
                            <Hidden only={'xs'}>
                                <Typography variant="body_500" color={palette.error[500]}>
                                    Remove
                                </Typography>
                            </Hidden>
                        </IconButton>
                    }
                </Box >
                {isLoading ? (
                    <Grid
                        container
                        rowGap={{
                            xs: '5px',
                            sm: '10px',
                            md: '15px',
                            lg: '20px'
                        }}
                    >
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Skeleton variant="text" animation="wave" width={100} />
                            <Skeleton variant="text" animation="wave" width={100} />
                        </Grid>
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Skeleton variant="text" animation="wave" width={100} />
                            <Skeleton variant="text" animation="wave" width={100} />
                        </Grid>
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Skeleton variant="text" animation="wave" width={100} />
                            <Skeleton variant="text" animation="wave" width={100} />
                        </Grid>
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Skeleton variant="text" animation="wave" width={100} />
                            <Skeleton variant="text" animation="wave" width={100} />
                        </Grid>
                        {isSwitchScreen ?
                            <Grid
                                item
                                lg={3}
                                md={6}
                                sm={6}
                                xs={12}
                                sx={{
                                    justifyContent: 'space-between',
                                    display: 'flex',
                                    flexDirection: {
                                        xs: 'row',
                                        sm: 'column',
                                        md: 'column',
                                        lg: 'column'
                                    },
                                }}
                            >
                                <Skeleton variant="text" animation="wave" width={100} />
                                <Skeleton variant="text" animation="wave" width={100} />
                            </Grid> : null
                        }
                    </Grid>
                ) : (
                    <Grid
                        container
                        rowGap={{
                            xs: '5px',
                            sm: '10px',
                            md: '15px',
                            lg: '20px'
                        }}
                    >
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Typography
                                variant='subtitle_400'
                                color={palette.text.secondary}
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                            >
                                Folio
                            </Typography>
                            <Typography
                                variant='body_500'
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                                color={palette.text.primary}
                            >
                                {outgoingSchDetails?.folioNo ?? ""}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Typography
                                variant='subtitle_400'
                                color={palette.text.secondary}
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                            >
                                NAV: {outgoingSchDetails?.navDate ?? ""}
                            </Typography>
                            <Typography
                                variant='body_500'
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                                color={palette.text.primary}
                            >
                                {formatCurrency({ value: outgoingSchDetails?.navValue ?? "", locale, currencyCode })}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Typography
                                variant='subtitle_400'
                                color={palette.text.secondary}
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                            >
                                Balance Unit
                            </Typography>
                            <Typography
                                variant='body_500'
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                                color={palette.text.primary}
                            >
                                {Number(outgoingSchDetails?.units ?? "")}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            lg={3}
                            md={6}
                            sm={6}
                            xs={12}
                            sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column', md: 'column', lg: 'column' },
                            }}
                        >
                            <Typography
                                variant='subtitle_400'
                                color={palette.text.secondary}
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                            >
                                Balance Amount
                            </Typography>
                            <Typography
                                variant='body_500'
                                fontSize={{
                                    xs: '14px',
                                    sm: '14px',
                                    md: '16px',
                                    lg: '16px'
                                }}
                                color={palette.text.primary}
                            >
                                {formatCurrency({ value: outgoingSchDetails?.balanceAmount, locale, currencyCode })}
                            </Typography>
                        </Grid>
                        {isSwitchScreen ?
                            <Grid
                                item
                                lg={3}
                                md={6}
                                sm={6}
                                xs={12}
                                sx={{
                                    justifyContent: 'space-between',
                                    display: 'flex',
                                    flexDirection: {
                                        xs: 'row',
                                        sm: 'column',
                                        md: 'column',
                                        lg: 'column'
                                    },
                                }}
                            >
                                <>
                                    <Typography
                                        fontSize={{
                                            xs: '14px',
                                            sm: '14px',
                                            md: '16px',
                                            lg: '16px'
                                        }}
                                        variant='subtitle_400'
                                        color={palette.text.secondary}
                                    >
                                        Free Units
                                        <Tooltip
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        margin: "0px !important",
                                                        backgroundColor: "transparent"
                                                    }
                                                }
                                            }}
                                            title={
                                                <div
                                                    style={{
                                                        borderRadius: '10px', backgroundColor: '#0F131F', padding: '15px'
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            color: palette.primary[500],
                                                            fontWeight: '600',
                                                            fontSize: '16px'
                                                        }}
                                                    >
                                                        Free units

                                                    </span>
                                                    <br />
                                                    <span style={{ ...typography.subtitle_500, fontFamily: typography.fontFamily }}>Units that are not in Lock-in.</span>
                                                </div>
                                            }
                                        >
                                            <IconButton>
                                                <InfoIconOutlined />
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                </>
                                <Typography
                                    fontSize={{
                                        xs: '14px',
                                        sm: '14px',
                                        md: '16px',
                                        lg: '16px'
                                    }}
                                    variant='body_500'
                                    fontWeight={600}
                                    color={palette.text.primary}
                                >
                                    {outgoingSchDetails?.availableUnits ?? ""}
                                </Typography>
                            </Grid> : null
                        }
                    </Grid>
                )}
                {isSwitchScreen ? (
                    isSwitchTransactionNotAllowed ? (
                        <Box m={'auto auto'}>
                            <Typography
                                variant="h6"
                                color={palette.error[500]}
                                sx={{ textAlign: 'center', m: 4 }}
                            >
                                Switch transaction is not allowed
                            </Typography>
                        </Box>
                    ) : (
                        <IncomingSchemeCard
                            setFromSchemeDialogOpen={setFromSchemeDialogOpen}
                            isLoading={isLoading}
                            schemeLoading={schemeLoading}
                            id={id}
                            handleChangeScheme={handleChangeScheme}
                            isSubmitting={isSubmitting}
                        />
                    )
                ) : (
                    <IncomingSchemeCard
                        setFromSchemeDialogOpen={setFromSchemeDialogOpen}
                        schemeLoading={schemeLoading}
                        isLoading={isLoading}
                        id={id}
                        handleChangeScheme={handleChangeScheme}
                        isSubmitting={isSubmitting}
                    />
                )}
                {
                    open && (
                        <AlertModel {...{ open, setOpen, title: 'Data not saved, will be lost!', desc: 'Going back will result in loss of your entered data. Are you sure you want to proceed?' }} />
                    )
                }
            </Box >
            {
                isFromSchemeDialogOpen && (
                    <FromSchemeDialog
                        open={isFromSchemeDialogOpen}
                        onClose={() => setFromSchemeDialogOpen(false)}
                        isSwitchScreen={isSwitchScreen}
                        outgoingSchDetails={outgoingSchDetails}
                        clientCode={navState.clientCode}
                        handleProcess={(schemeId: string, divReinvestAllowed?: any) => {
                            getValidation(id, schemeId, divReinvestAllowed)
                        }}
                    />
                )
            }
        </>
    )
}

export default Card